import { useState } from 'react'
import { supabase } from '../supabase'

const ACCESS_CODES = {
  'BOOK2027': { days: 90, type: 'book', tier: 'pro' },
  'BETA2027': { days: 30, type: 'beta', tier: 'pro' },
}

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: 700, color: '#00712D' }}>The Application Multiplier</div>
      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>60-Day Job Search System</div>
    </div>
  )
}

function Card({ children, title, subtitle }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <Logo />
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '2rem' }}>
          <h1 style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.4rem' }}>{title}</h1>
          {subtitle && <p style={{ color: '#555', fontSize: '0.875rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  )
}

async function createAccount(email, password, accessType, tier, days) {
  const { data, error } = await supabase.auth.signUp({ email: email.trim().toLowerCase(), password })
  if (error) throw error
  if (data?.user) {
    const trialEnd = new Date()
    trialEnd.setDate(trialEnd.getDate() + days)
    await supabase.from('profiles').insert({
      id: data.user.id, email: email.trim().toLowerCase(),
      access_type: accessType, tier, trial_end: trialEnd.toISOString(),
      onboarding_complete: false, day_started: new Date().toISOString(),
      active_days: 0, current_day: 1,
    })
  }
  return data
}

function Landing({ onTrial, onCode, onLogin }) {
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
      <Logo />
      <p style={{ color: '#555', fontSize: '1rem', maxWidth: '380px', lineHeight: 1.7, marginBottom: '2.5rem' }}>
        A 60-day referral-first job search system built by a recruiter with 15 years of experience. Stop cold applying. Start getting referred.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', maxWidth: '320px' }}>
        <button className="btn-primary" style={{ padding: '0.95rem', fontSize: '1rem' }} onClick={onTrial}>Start free 10-day trial</button>
        <button className="btn-secondary" style={{ padding: '0.95rem' }} onClick={onCode}>I have an access code</button>
        <button className="btn-ghost" onClick={onLogin}>Already have an account? Log in</button>
      </div>
      <div style={{ marginTop: '2.5rem', padding: '1.25rem', background: '#E8F2EC', borderRadius: '8px', maxWidth: '380px', width: '100%', fontSize: '0.85rem', color: '#555', lineHeight: 1.7 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Access Options</div>
        📖 <strong>Have the book?</strong> Find your code inside the front cover — 90 days free<br /><br />
        🧪 <strong>Beta tester?</strong> Use the code from your invitation — 30 days free<br /><br />
        ✨ <strong>Just exploring?</strong> 10-day free trial, no code needed
      </div>
    </div>
  )
}

function CodeSignup({ onBack }) {
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function handleSubmit() {
    const trimmed = code.trim().toUpperCase()
    const access = ACCESS_CODES[trimmed]
    if (!access) { setError('Invalid access code. Check your book or invitation email.'); return }
    if (!email.includes('@')) { setError('Please enter a valid email address.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    setLoading(true); setError('')
    try {
      const data = await createAccount(email, password, access.type, access.tier, access.days)
      if (data?.session) setMessage('Welcome! Setting up your account...')
      else setMessage('Check your email and click the confirmation link, then log in.')
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <Card title="Enter your access code" subtitle="Find your code inside The Application Multiplier or in your beta invite email.">
      {error && <div className="error-msg">{error}</div>}
      {message && <div className="success-msg">{message}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Access Code</label>
          <input className="input-field" placeholder="Enter your access code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} autoComplete="off" autoCorrect="off" spellCheck={false} style={{ letterSpacing: '0.1em' }} />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Email</label>
          <input className="input-field" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoCapitalize="off" />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Password</label>
          <input className="input-field" type="password" placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>
        <button className="btn-primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Creating account...' : 'Create account'}</button>
        <button className="btn-ghost" onClick={onBack}>← Back</button>
      </div>
    </Card>
  )
}

function TrialSignup({ onBack }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function handleSubmit() {
    if (!email.includes('@')) { setError('Please enter a valid email address.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    setLoading(true); setError('')
    try {
      const data = await createAccount(email, password, 'trial', 'basic', 10)
      if (data?.session) setMessage('Welcome! Setting up your account...')
      else setMessage('Check your email and click the confirmation link, then log in.')
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <Card title="Start your free trial" subtitle="10 days free, no credit card required.">
      {error && <div className="error-msg">{error}</div>}
      {message && <div className="success-msg">{message}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Email</label>
          <input className="input-field" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoCapitalize="off" />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Password</label>
          <input className="input-field" type="password" placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>
        <button className="btn-primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Creating account...' : 'Start free trial'}</button>
        <p style={{ fontSize: '0.78rem', color: '#aaa', textAlign: 'center' }}>After 10 days: $25 for 6 months. Cancel anytime.</p>
        <button className="btn-ghost" onClick={onBack}>← Back</button>
      </div>
    </Card>
  )
}

function Login({ onBack }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resetSent, setResetSent] = useState(false)

  async function handleLogin() {
    if (!email || !password) { setError('Please enter your email and password.'); return }
    setLoading(true); setError('')
    const { error: loginError } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password })
    if (loginError) setError('Invalid email or password. Please try again.')
    setLoading(false)
  }

  async function handleReset() {
    if (!email) { setError('Enter your email address above first.'); return }
    await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo: window.location.origin })
    setResetSent(true)
  }

  return (
    <Card title="Welcome back" subtitle="Log in to continue your job search.">
      {error && <div className="error-msg">{error}</div>}
      {resetSent && <div className="success-msg">Password reset email sent! Check your inbox.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Email</label>
          <input className="input-field" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoCapitalize="off" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>Password</label>
          <input className="input-field" type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        </div>
        <button className="btn-primary" onClick={handleLogin} disabled={loading}>{loading ? 'Logging in...' : 'Log in'}</button>
        <button className="btn-ghost" onClick={handleReset} style={{ fontSize: '0.78rem', color: '#aaa' }}>Forgot password?</button>
        <button className="btn-ghost" onClick={onBack}>← Back</button>
      </div>
    </Card>
  )
}

export default function Auth() {
  const [mode, setMode] = useState('landing')
  if (mode === 'landing') return <Landing onTrial={() => setMode('trial')} onCode={() => setMode('signup')} onLogin={() => setMode('login')} />
  if (mode === 'signup') return <CodeSignup onBack={() => setMode('landing')} />
  if (mode === 'trial') return <TrialSignup onBack={() => setMode('landing')} />
  if (mode === 'login') return <Login onBack={() => setMode('landing')} />
  return null
}
